/**
 * Saves a locally-generated file (e.g. a PDF from react-native-html-to-pdf) somewhere
 * the user can actually find it.
 *
 * react-native-share's Share.open() only opens a chooser for handing the file to
 * another app — it never saves anything on its own, so if the user dismisses that
 * chooser (or picks nothing suitable), the file effectively vanishes. On Android,
 * this instead copies the file into the public Downloads folder and registers it
 * with the OS DownloadManager, so it shows a normal "download complete" notification
 * and appears in the Downloads app, matching what a "Download" button is expected to do.
 *
 * iOS has no equivalent public Downloads folder, so the share sheet (with its
 * "Save to Files" option) remains the standard, expected pattern there.
 */
import { Platform } from 'react-native';
import Share from 'react-native-share';
import { Permissions } from './permissions';
import { Logger } from './logger';

let ReactNativeBlobUtil: any;
if (Platform.OS === 'android') {
  try {
    ReactNativeBlobUtil = require('react-native-blob-util').default || require('react-native-blob-util');
  } catch (e) {
    Logger.error('downloadFile', 'Failed to load react-native-blob-util', e);
  }
}

export interface SaveResult {
  /** True if the file was actually saved to a location outside the app sandbox (Downloads on Android). */
  saved: boolean;
  message: string;
}

export async function saveGeneratedFile(
  sourcePath: string,
  fileName: string,
  mimeType: string = 'application/pdf'
): Promise<SaveResult> {
  if (Platform.OS === 'android' && ReactNativeBlobUtil) {
    try {
      // Runtime permission is only required on Android 9 (API 28) and below —
      // DownloadManager on 10+ doesn't need WRITE_EXTERNAL_STORAGE.
      if (typeof Platform.Version === 'number' && Platform.Version < 29) {
        await Permissions.requestStorage();
      }

      const destPath = `${ReactNativeBlobUtil.fs.dirs.DownloadDir}/${fileName}`;
      await ReactNativeBlobUtil.fs.cp(sourcePath, destPath);

      await ReactNativeBlobUtil.android.addCompleteDownload({
        title: fileName,
        description: 'Download complete',
        mime: mimeType,
        path: destPath,
        showNotification: true,
      });

      return { saved: true, message: `Saved to Downloads/${fileName}` };
    } catch (err) {
      Logger.error('downloadFile', 'Failed to save to Downloads, falling back to share sheet', err);
      // Fall through to the share-sheet fallback below.
    }
  }

  try {
    await Share.open({
      url: `file://${sourcePath}`,
      title: fileName,
      type: mimeType,
    });
  } catch (shareErr) {
    // Dismissing the share sheet without picking an app is not a failure —
    // the file was still generated successfully.
  }

  return { saved: false, message: 'Opened share sheet' };
}

/** Same as saveGeneratedFile, but for content already held as a base64 string (e.g. a fetched attachment) rather than a file already on disk. */
export async function saveGeneratedFileFromBase64(
  base64Data: string,
  fileName: string,
  mimeType: string = 'application/octet-stream'
): Promise<SaveResult> {
  if (Platform.OS === 'android' && ReactNativeBlobUtil) {
    try {
      if (typeof Platform.Version === 'number' && Platform.Version < 29) {
        await Permissions.requestStorage();
      }

      const destPath = `${ReactNativeBlobUtil.fs.dirs.DownloadDir}/${fileName}`;
      await ReactNativeBlobUtil.fs.writeFile(destPath, base64Data, 'base64');

      await ReactNativeBlobUtil.android.addCompleteDownload({
        title: fileName,
        description: 'Download complete',
        mime: mimeType,
        path: destPath,
        showNotification: true,
      });

      return { saved: true, message: `Saved to Downloads/${fileName}` };
    } catch (err) {
      Logger.error('downloadFile', 'Failed to save to Downloads, falling back to share sheet', err);
    }
  }

  try {
    await Share.open({
      url: `data:${mimeType};base64,${base64Data}`,
      title: fileName,
      filename: fileName.replace(/\.[^/.]+$/, ''),
    });
  } catch (shareErr) {
    // Dismissing the share sheet without picking an app is not a failure.
  }

  return { saved: false, message: 'Opened share sheet' };
}
